/**
 * Employee Self-Service Portal
 * Unified hub for employees to access payslips and submit reimbursement claims
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { payrollAPI, expenseAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Download, Wallet, Receipt, FileText, Plus, Clock, CheckCircle,
  XCircle, TrendingUp, Banknote, Calendar, ChevronRight, Loader2
} from 'lucide-react';

const EXPENSE_CATEGORIES = ['Travel', 'Meals', 'Supplies', 'Equipment', 'Software', 'Training', 'Other'];

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

const STATUS_CONFIG = {
  pending: { label: 'Pending', variant: 'secondary', icon: Clock },
  manager_approved: { label: 'Manager Approved', variant: 'outline', icon: CheckCircle },
  approved: { label: 'Approved', variant: 'default', icon: CheckCircle },
  rejected: { label: 'Rejected', variant: 'destructive', icon: XCircle },
  paid: { label: 'Paid', variant: 'default', icon: Banknote },
};

export default function EmployeePortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [payrollData, setPayrollData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loadingPayroll, setLoadingPayroll] = useState(true);
  const [loadingExpenses, setLoadingExpenses] = useState(true);
  const [downloading, setDownloading] = useState({});
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [submittingExpense, setSubmittingExpense] = useState(false);
  
  const [expenseForm, setExpenseForm] = useState({
    amount: '',
    category: 'Travel',
    description: '',
    receipt_url: ''
  });

  // Fetch payroll data
  const fetchPayroll = async () => {
    setLoadingPayroll(true);
    try {
      const res = await payrollAPI.getMyPayroll({ month: currentMonth, year: currentYear });
      setPayrollData(res.data);
    } catch (error) {
      console.error('Failed to fetch payroll:', error);
    } finally {
      setLoadingPayroll(false);
    }
  };

  // Fetch expense claims
  const fetchExpenses = async () => {
    setLoadingExpenses(true);
    try {
      const res = await expenseAPI.getAll();
      setExpenses(res.data || []);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    } finally {
      setLoadingExpenses(false);
    }
  };

  useEffect(() => {
    fetchPayroll();
    fetchExpenses();
  }, []);

  // Handle payslip download
  const handleDownloadPayslip = async (month, year) => {
    const key = `${month}-${year}`;
    setDownloading(prev => ({ ...prev, [key]: true }));
    try {
      const response = await payrollAPI.downloadPayslip(month, year);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const monthName = months.find(m => m.value === month)?.label || month;
      link.download = `Payslip_${monthName}_${year}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Payslip downloaded successfully');
    } catch (error) {
      toast.error('Failed to download payslip');
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Handle expense submission
  const handleSubmitExpense = async (e) => {
    e.preventDefault();
    setSubmittingExpense(true);
    try {
      await expenseAPI.create({
        ...expenseForm,
        amount: parseFloat(expenseForm.amount)
      });
      toast.success('Expense claim submitted successfully');
      setExpenseDialogOpen(false);
      setExpenseForm({ amount: '', category: 'Travel', description: '', receipt_url: '' });
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit expense claim');
    } finally {
      setSubmittingExpense(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Calculate expense stats
  const expenseStats = useMemo(() => {
    const pending = expenses.filter(e => e.status === 'pending' || e.status === 'manager_approved').length;
    const approved = expenses.filter(e => e.status === 'approved' || e.status === 'paid').length;
    const rejected = expenses.filter(e => e.status === 'rejected').length;
    const totalAmount = expenses
      .filter(e => e.status === 'approved' || e.status === 'paid')
      .reduce((sum, e) => sum + (e.amount || 0), 0);
    return { pending, approved, rejected, totalAmount };
  }, [expenses]);

  // Recent payslips (last 6 months)
  const recentPayslips = useMemo(() => {
    const slips = [];
    for (let i = 0; i < 6; i++) {
      let month = currentMonth - i;
      let year = currentYear;
      if (month <= 0) {
        month += 12;
        year -= 1;
      }
      slips.push({ month, year, monthName: months.find(m => m.value === month)?.label });
    }
    return slips;
  }, []);

  const recentExpenses = useMemo(() => {
    return expenses.slice(0, 5);
  }, [expenses]);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Employee Self-Service Portal</h1>
        <p className="text-muted-foreground mt-1">
          Access your payslips and manage reimbursement claims
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Payslips Card */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Payslips</CardTitle>
                  <CardDescription>Download your monthly payslips</CardDescription>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/my-payroll')}
              >
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingPayroll ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {recentPayslips.slice(0, 3).map(slip => {
                  const key = `${slip.month}-${slip.year}`;
                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent"
                    >
                      <div className="flex items-center gap-3">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{slip.monthName} {slip.year}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownloadPayslip(slip.month, slip.year)}
                        disabled={downloading[key]}
                      >
                        {downloading[key] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reimbursement Claims Card */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Receipt className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <CardTitle>Reimbursement Claims</CardTitle>
                  <CardDescription>Submit and track expense claims</CardDescription>
                </div>
              </div>
              <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="w-4 h-4 mr-1" />
                    New Claim
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Submit Expense Claim</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmitExpense} className="space-y-4">
                    <div>
                      <Label htmlFor="amount">Amount (₹)</Label>
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        required
                        value={expenseForm.amount}
                        onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <Select
                        value={expenseForm.category}
                        onValueChange={(value) => setExpenseForm({ ...expenseForm, category: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {EXPENSE_CATEGORIES.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        required
                        value={expenseForm.description}
                        onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                        placeholder="Enter expense details..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="receipt_url">Receipt URL (Optional)</Label>
                      <Input
                        id="receipt_url"
                        type="url"
                        value={expenseForm.receipt_url}
                        onChange={(e) => setExpenseForm({ ...expenseForm, receipt_url: e.target.value })}
                        placeholder="https://..."
                      />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setExpenseDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={submittingExpense}>
                        {submittingExpense ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          'Submit Claim'
                        )}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {/* Expense Stats */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <div className="text-2xl font-bold text-amber-600">{expenseStats.pending}</div>
                <div className="text-xs text-muted-foreground">Pending</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{expenseStats.approved}</div>
                <div className="text-xs text-muted-foreground">Approved</div>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{expenseStats.rejected}</div>
                <div className="text-xs text-muted-foreground">Rejected</div>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/expenses')}
            >
              View All Claims <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Payslips Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              Recent Payslips
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingPayroll ? (
              <div className="space-y-2">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentPayslips.map(slip => {
                    const key = `${slip.month}-${slip.year}`;
                    return (
                      <TableRow key={key}>
                        <TableCell className="font-medium">{slip.monthName}</TableCell>
                        <TableCell>{slip.year}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownloadPayslip(slip.month, slip.year)}
                            disabled={downloading[key]}
                          >
                            {downloading[key] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-1" />
                                Download
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Recent Expense Claims */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Recent Expense Claims
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingExpenses ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : recentExpenses.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Receipt className="w-12 h-12 mx-auto mb-2 opacity-20" />
                <p>No expense claims yet</p>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-4"
                  onClick={() => setExpenseDialogOpen(true)}
                >
                  Submit Your First Claim
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {recentExpenses.map(expense => {
                  const config = STATUS_CONFIG[expense.status] || STATUS_CONFIG.pending;
                  const Icon = config.icon;
                  return (
                    <div
                      key={expense.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{expense.category}</span>
                          <Badge variant={config.variant} className="text-xs">
                            <Icon className="w-3 h-3 mr-1" />
                            {config.label}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground truncate">
                          {expense.description}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {formatDate(expense.created_at)}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <div className="font-semibold">{formatCurrency(expense.amount)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Current Month Summary */}
      {!loadingPayroll && payrollData && (
        <Card>
          <CardHeader>
            <CardTitle>Current Month Salary Summary</CardTitle>
            <CardDescription>
              {months.find(m => m.value === currentMonth)?.label} {currentYear}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Gross Salary</div>
                <div className="text-2xl font-bold text-blue-600">
                  {formatCurrency(payrollData.current_month?.gross_salary || 0)}
                </div>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Total Deductions</div>
                <div className="text-2xl font-bold text-red-600">
                  {formatCurrency(payrollData.current_month?.total_deductions || 0)}
                </div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Net Salary</div>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(payrollData.current_month?.net_salary || 0)}
                </div>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">YTD Earnings</div>
                <div className="text-2xl font-bold text-purple-600">
                  {formatCurrency(payrollData.ytd_summary?.gross || 0)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
